'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { EASE, Eyebrow, HAIRLINE, useReveal } from '@/components/page-kit'
import { SOLUTIONS } from '@/config/solutions'

/* Shared shell for the two bespoke solution pages. Everything here is
   presentation — all copy is passed in from @/config/solutions, so neither page
   can introduce wording that is not authored.

   Each page brings its own hero (cloud = the engraved plate, ai = the filament
   field) and its own body device (cloud = the stack, ai = the loop); the essay
   and the close are shared, because the two are siblings in one series. */

export type SolutionSlug = 'cloud-infrastructure' | 'ai-infrastructure'

/** Client components resolve their own data: lucide icons live on these
    objects, and a component reference cannot cross the server/client
    boundary as a prop. The page passes a slug string and nothing else. */
export function useSolution(slug: SolutionSlug): Solution {
  return SOLUTIONS[slug] as Solution
}

export type Solution = {
  slug: string
  badge: string
  number: string
  title: string
  /** Page h1 only — see the note in @/config/solutions. Falls back to title. */
  heading?: string
  subtitle: string
  tagline: string
  intro: string
  body1: string
  body2: string
  body3: string
  closing: string
  deliversHeading: string
  deliversAccent: string
  delivers: {
    icon: React.ComponentType<{ className?: string }>
    title: string
    text: string
  }[]
  howItWorks: {
    icon: React.ComponentType<{ className?: string }>
    step: string
    title: string
    text: string
  }[]
  useCases: {
    icon: React.ComponentType<{ className?: string }>
    title: string
    text: string
  }[]
  images: Record<string, string>
}

/* ─── The essay ────────────────────────────────────────────────────────── */
/* intro / body1 / body2 / body3 / closing are five authored paragraphs. The
   legacy template scattered them between stock photos; here they are set as one
   continuous argument, with the opening sentence lifted as the heading. */
export function SolutionProse({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const block = useReveal<HTMLDivElement>(0.12)

  const [opening, ...introRest] = solution.intro.split(/(?<=\.)\s+/)
  const paragraphs = [
    introRest.join(' '),
    solution.body1,
    solution.body2,
    solution.body3,
  ].filter(Boolean)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <div ref={block.ref}>
          <Eyebrow>Overview</Eyebrow>

          <h2
            className="font-heading mt-6 max-w-4xl text-2xl font-normal leading-[1.3] tracking-tight text-foreground sm:text-3xl lg:text-[40px] lg:leading-[1.25]"
            style={{
              opacity: block.shown ? 1 : 0,
              transform: block.shown ? 'none' : 'translateY(12px)',
              transition: `opacity 700ms ${EASE} 60ms, transform 700ms ${EASE} 60ms`,
            }}
          >
            <ChromaText>{opening}</ChromaText>
          </h2>

          <div className="mt-8 grid gap-x-12 gap-y-6 lg:grid-cols-2">
            {paragraphs.map((p, i) => (
              <p
                key={i}
                className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-[17px]"
                style={{
                  opacity: block.shown ? 1 : 0,
                  transform: block.shown ? 'none' : 'translateY(10px)',
                  transition: `opacity 600ms ${EASE} ${180 + i * 90}ms, transform 600ms ${EASE} ${180 + i * 90}ms`,
                }}
              >
                {p}
              </p>
            ))}
          </div>

          {/* The authored closing paragraph, given its own weight. */}
          <div
            className={cn('mt-10 border-t pt-8', HAIRLINE)}
            style={{
              opacity: block.shown ? 1 : 0,
              transition: `opacity 700ms ${EASE} 640ms`,
            }}
          >
            <p className="font-heading max-w-4xl text-lg font-normal leading-relaxed text-foreground sm:text-xl lg:text-2xl lg:leading-[1.45]">
              {solution.closing}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Use cases ────────────────────────────────────────────────────────── */
export function SolutionUseCases({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  const grid = useReveal<HTMLDivElement>(0.12)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <Eyebrow>Use cases</Eyebrow>

        <div ref={grid.ref} className="mt-8 sm:mt-10">
          <div className="grid grid-cols-1 gap-px bg-border/70 sm:grid-cols-2">
            {solution.useCases.map((u, i) => {
              const Glyph = u.icon
              return (
                <article
                  key={u.title}
                  className="relative bg-background px-0 py-7 sm:px-7 sm:py-9"
                  style={{
                    opacity: grid.shown ? 1 : 0,
                    transform: grid.shown ? 'none' : 'translateY(10px)',
                    transition: `opacity 500ms ${EASE} ${i * 90}ms, transform 500ms ${EASE} ${i * 90}ms`,
                  }}
                >
                  <div className="flex items-baseline gap-3 pr-8">
                    <span className="text-[11px] font-semibold tabular-nums text-muted-foreground/60">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                      {u.title}
                    </h3>
                  </div>
                  <p className="mt-3 max-w-md pl-[26px] text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {u.text}
                  </p>

                  {/* Bare glyph demoted to the corner — never an icon in a
                      tinted rounded square. */}
                  <Glyph
                    aria-hidden
                    className="absolute right-0 top-7 size-5 text-foreground/30 sm:right-7 sm:top-9"
                  />
                </article>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Close ────────────────────────────────────────────────────────────── */
export function SolutionCta({ slug }: { slug: SolutionSlug }) {
  const solution = useSolution(slug)
  return (
    <section className="relative mt-16 overflow-hidden sm:mt-20 md:mt-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[80%]"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div className="container relative z-10 mx-auto py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl">
          <Eyebrow>Ready when you are</Eyebrow>

          <h2 className="font-heading mt-6 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[52px] lg:leading-[1.1]">
            <ChromaText>{solution.tagline}</ChromaText>
          </h2>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {solution.subtitle}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Schedule a conversation</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="/architecture">See the architecture</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Heading for the "delivers" section. `deliversHeading` and `deliversAccent`
 * are both authored — the accent word simply takes the brand colour.
 */
export function DeliversHeading({ solution }: { solution: Solution }) {
  return (
    <h2 className="font-heading mt-4 text-2xl font-normal leading-tight text-foreground sm:text-3xl md:text-4xl lg:text-5xl">
      <ChromaText>
        {solution.deliversHeading}{' '}
        <span className="text-emerald-600 dark:text-primary-active">
          {solution.deliversAccent}
        </span>
      </ChromaText>
    </h2>
  )
}
